/**
 * @file src/store.js
 * @description Redux store — minimal configuration.
 *
 * What's here:
 *  - invoiceReducer  — real-time SSE invoice processing progress
 *  - sseMiddleware   — SSE connection management + event dispatching
 *
 * What was removed and why:
 *  - userReducer (loginSlice) — auth state is now managed by React Query
 *    via getAuthenticatedUser() + QUERY_KEYS.auth.me(). Having both Redux
 *    and React Query managing auth creates two sources of truth which leads
 *    to stale state bugs (e.g. Redux says logged in, React Query says 401).
 *
 * What was removed and why (persistence):
 *  - redux-persist removed entirely. Invoice progress is the only state
 *    that needs cross-session survival and it is handled explicitly via:
 *    1. saveInvoiceProgress() API call in sseMiddleware on "submitted" event
 *    2. keepalive fetch in the invoice-processing-queue page on beforeunload
 *    3. getInvoiceProgress() restore on mount of invoice-processing-queue
 *    Persisting the Redux store to localStorage is no longer needed.
 *
 * Rules:
 *  - No page or component imports here
 *  - No async logic — all side effects in middleware
 */

import { configureStore } from "@reduxjs/toolkit";
import invoiceProgressReducer from "pages/invoice-processing-queue/invoiceProgressSlice";
import sseMiddleware from "./lib/sseMiddleware";

export const store = configureStore({
  reducer: {
    invoiceReducer: invoiceProgressReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        /**
         * SSE EventSource is not serializable — ignore the middleware
         * action types that carry it internally.
         */
        ignoredActions: ["invoiceSlice/addNewInvoice"],
      },
    }).concat(sseMiddleware),

  /**
   * Enable Redux DevTools in development only.
   * In production this reduces bundle size and prevents
   * state from being inspectable in the browser.
   */
  devTools: import.meta.env.DEV,
});

export default store;
